import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, Edit2Icon, CopyIcon, Trash2Icon, EyeIcon, PinIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { useProjects } from '../../lib/store';
import { Button } from '../../components/ui/Button';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
export function WorkEditor() {
  const {
    projects,
    deleteProject,
    saveProject,
    reorderProjects
  } = useProjects();
  const navigate = useNavigate();
  const [historyOpen, setHistoryOpen] = useState(false);
  const handleDuplicate = (project: any) => {
    const newProject = {
      ...project,
      id: Math.random().toString(36).substr(2, 9),
      title: `${project.title} (Copy)`,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    saveProject(newProject);
  };
  const togglePin = (project: any) => {
    saveProject({
      ...project,
      featured: !project.featured,
      updatedAt: new Date().toISOString()
    });
  };
  // Sort projects by order field
  const sortedProjects = [...projects].sort((a, b) => {
    return (a.order ?? 999) - (b.order ?? 999);
  });
  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === sortedProjects.length - 1) {
      return;
    }
    const newItems = [...sortedProjects];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newItems[index];
    newItems[index] = newItems[targetIndex];
    newItems[targetIndex] = temp;
    const reorderedItems = newItems.map((item, i) => ({
      ...item,
      order: i
    }));
    reorderProjects(reorderedItems);
  };
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-1580">
      <div className="flex items-center justify-between mb-8" data-id="element-1581">
        <div data-id="element-1582">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-1583">
            Work / Projects
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-1584">
            Manage your portfolio case studies.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-1585">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-1586" />
          <Button onClick={() => navigate('/admin/work/new')} className="gap-2" data-id="element-1587">
            <PlusIcon className="w-4 h-4" data-id="element-1588" />
            New Project
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border" data-id="element-1589">
        <div className="grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-4 p-4 border-b border-border font-mono text-xs uppercase tracking-widest text-muted-foreground" data-id="element-1590">
          <div className="w-16" data-id="element-1591">Order</div>
          <div className="w-12" data-id="element-1592">No.</div>
          <div data-id="element-1593">Project Details</div>
          <div className="w-24" data-id="element-1594">Status</div>
          <div className="w-32" data-id="element-1595">Last Updated</div>
          <div className="w-12 text-center" data-id="element-1596">Pin</div>
          <div className="w-48 text-right" data-id="element-1597">Actions</div>
        </div>

        <div className="divide-y divide-border" data-id="element-1598">
          {sortedProjects.length === 0 ? <div className="p-8 text-center text-muted-foreground font-mono text-sm" data-id="element-1599">
              No projects found. Create your first case study.
            </div> : sortedProjects.map((project, index) => <div key={project.id} className="grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-4 p-4 items-center hover:bg-secondary/50 transition-colors" data-id="element-1600">
                <div className="w-16 flex flex-col gap-1" data-id="element-1601">
                  <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1602">
                    <ArrowUpIcon className="w-4 h-4" data-id="element-1603" />
                  </button>
                  <button onClick={() => moveItem(index, 'down')} disabled={index === sortedProjects.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1604">
                    <ArrowDownIcon className="w-4 h-4" data-id="element-1605" />
                  </button>
                </div>

                <div className="w-12 font-display text-xl font-bold text-muted-foreground" data-id="element-1606">
                  {project.num}
                </div>

                <div data-id="element-1607">
                  <h3 className="font-display text-lg font-bold" data-id="element-1608">
                    {project.title}
                  </h3>
                  <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-1609">
                    {project.role} · {project.year}
                  </p>
                </div>

                <div className="w-24 flex flex-col gap-1" data-id="element-1610">
                  <span className={`inline-flex items-center justify-center px-2 py-1 font-mono text-[10px] uppercase tracking-widest border ${project.status === 'published' ? 'border-primary/50 text-primary bg-primary/10' : 'border-border text-muted-foreground bg-secondary'}`} data-id="element-1611">
                    {project.status}
                  </span>
                </div>

                <div className="w-32 font-mono text-xs text-muted-foreground" data-id="element-1612">
                  {new Date(project.updatedAt).toLocaleDateString()}
                </div>

                <div className="w-12 flex justify-center" data-id="element-1613">
                  <button onClick={() => togglePin(project)} className={`p-1.5 rounded-md transition-colors ${project.featured ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:bg-secondary'}`} title={project.featured ? 'Unpin' : 'Pin to top'} data-id="element-1614">
                    <PinIcon className="w-4 h-4" data-id="element-1615" />
                  </button>
                </div>

                <div className="w-48 flex justify-end gap-2" data-id="element-1616">
                  <Button variant="ghost" size="sm" className="px-2" onClick={() => window.open(`/project/${project.slug || project.id}`, '_blank')} title="Preview" data-id="element-1617">
                    <EyeIcon className="w-4 h-4" data-id="element-1618" />
                  </Button>
                  <Button variant="ghost" size="sm" className="px-2" onClick={() => navigate(`/admin/work/${project.id}`)} title="Edit" data-id="element-1619">
                    <Edit2Icon className="w-4 h-4" data-id="element-1620" />
                  </Button>
                  <Button variant="ghost" size="sm" className="px-2" onClick={() => handleDuplicate(project)} title="Duplicate" data-id="element-1621">
                    <CopyIcon className="w-4 h-4" data-id="element-1622" />
                  </Button>
                  <Button variant="ghost" size="sm" className="px-2 text-destructive hover:text-destructive" onClick={() => {
              if (window.confirm('Are you sure you want to delete this project?')) {
                deleteProject(project.id);
              }
            }} title="Delete" data-id="element-1623">
                    <Trash2Icon className="w-4 h-4" data-id="element-1624" />
                  </Button>
                </div>
              </div>)}
        </div>
      </div>
      <VersionHistoryPanel contentKey="sinka_projects" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={data => {
      if (Array.isArray(data)) reorderProjects(data);
    }} data-id="element-1625" />
    </div>;
}